import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation } from "@apollo/client/react";
import { ClientDocument, LogClientContactDocument, OutreachQueueDocument } from "@/graphql/clients.generated";
import type { LoggedContact } from "@/pages/Alumni/components/LogContactForm";
import { CONTACT_TYPE_FOR_CHANNEL, API_OUTCOME_FOR_OUTCOME, toCalendarDate } from "@/pages/Alumni/helpers/client";

/**
 * Save a contact a coordinator logged, from wherever they logged it — the queue's row, the full
 * dialog, or the profile.
 *
 * The saved contact changes two things on screen, and both are re-read rather than patched: the
 * queue, where a logged contact moves the client's due date (usually out of the queue), and the
 * client's contact history. The queue and the profile on screen are refetched; a client's cached
 * history is dropped so the profile reads it fresh next time it opens.
 *
 * Rejects when the save fails, after saying so, so the form that called it keeps what was entered.
 */
export const useLogContact = () => {
    const { t } = useTranslation();
    const [logClientContact] = useMutation(LogClientContactDocument);

    /** `clientName` is for the confirmation; who the contact is saved against is `contact.clientId`. */
    return async (contact: LoggedContact, clientName: string): Promise<void> => {
        try {
            await logClientContact({
                variables: {
                    clientId: contact.clientId,
                    input: {
                        type: CONTACT_TYPE_FOR_CHANNEL[contact.channel],
                        outcome: API_OUTCOME_FOR_OUTCOME[contact.outcome],
                        notes: contact.note ?? null,
                        followUpDate: contact.followUpOn ? toCalendarDate(contact.followUpOn) : null,
                    },
                },
                refetchQueries: [
                    OutreachQueueDocument,
                    ClientDocument,
                ],
                awaitRefetchQueries: true,
                update: (cache) => {
                    cache.evict({
                        id: cache.identify({ __typename: "Client", id: contact.clientId }),
                        fieldName: "contacts",
                    });
                },
            });
        } catch (error) {
            toast.error(t("admin.alumni.logContact.failed", { name: clientName }));
            throw error;
        }
        toast.success(t("admin.alumni.logContact.logged", { name: clientName }));
    };
};
